// leaveSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { LeaveRequestState } from './leaveTypes';
import {
  createLeaveRequest,
  updateLeaveRequest,
  deleteLeaveRequest,
  fetchLeaveRequestById,
  fetchAllLeaveRequests,
  fetchLeaveRequestByEmployeeId,
  fetchLeaveRequestBySupervisor,
  fetchLeaveCancellationRequestBySupervisor,
  fetchHierarchyLeaveRequests
} from './leaveAction';

// Initial state
const initialState: LeaveRequestState = {
  leaveRequests: [],
  leaveRequestsBySupervisor: [],
  leaveCancellationRequestBySupervisor:[],
  employeeLeaveRequest:[],
  leaveRequestsByHodAndSupervisor: [],
  selectedLeaveRequest: undefined,
  loadingStates: {
    create: false,
    update: false,
    delete: false,
    fetchById: false,
    fetchSupervisor: false,
    fetchCancellationBySupervisor:false,
    fetchLeaveRequestByHodAndSupervisor: false,
    fetchAll: false,
    fetchByEmployeeId: false,  
  },
  errors: {
    create: undefined,
    update: undefined,
    delete: undefined,
    fetchById: undefined,
    fetchSupervisor: undefined,
    fetchLeaveRequestByHodAndSupervisor: undefined,
    fetchAll: undefined,
    fetchByEmployeeId: undefined,  
  },
};

// Slice
const leaveRequestSlice = createSlice({
  name: 'leaveRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create Leave Request
      .addCase(createLeaveRequest.pending, (state) => {
        state.loadingStates.create = true;
        state.errors.create = undefined;
      })
      .addCase(createLeaveRequest.fulfilled, (state, action) => {
        state.loadingStates.create = false;
        state.leaveRequests.push(action.payload);
      })
      .addCase(createLeaveRequest.rejected, (state, action) => {
        state.loadingStates.create = false;
        state.errors.create = action.payload as string;
      })

      // Update Leave Request
      .addCase(updateLeaveRequest.pending, (state) => {
        state.loadingStates.update = true;
        state.errors.update = undefined;
      })
      .addCase(updateLeaveRequest.fulfilled, (state, action) => {
        state.loadingStates.update = false;
        const index = state.leaveRequests.findIndex((leaveRequest) => leaveRequest.Id === action.payload.Id);
        if (index !== -1) {
          state.leaveRequests[index] = action.payload;
        }
      })
      .addCase(updateLeaveRequest.rejected, (state, action) => {
        state.loadingStates.update = false;
        state.errors.update = action.payload as string;
      })

      // Delete Leave Request
      .addCase(deleteLeaveRequest.pending, (state) => {
        state.loadingStates.delete = true;
        state.errors.delete = undefined;
      })
      .addCase(deleteLeaveRequest.fulfilled, (state, action) => {
        state.loadingStates.delete = false;
        state.leaveRequests = state.leaveRequests.filter((leaveRequest) => leaveRequest.Id !== action.payload.Id);
      })
      .addCase(deleteLeaveRequest.rejected, (state, action) => {
        state.loadingStates.delete = false;
        state.errors.delete = action.payload as string;
      })

      // Fetch Leave Request by ID
      .addCase(fetchLeaveRequestById.pending, (state) => {
        state.loadingStates.fetchById = true;
        state.errors.fetchById = undefined;
      })
      .addCase(fetchLeaveRequestById.fulfilled, (state, action) => {
        state.loadingStates.fetchById = false;
        state.leaveRequests = action.payload.data;
      })
      .addCase(fetchLeaveRequestById.rejected, (state, action) => {
        state.loadingStates.fetchById = false;
        state.errors.fetchById = action.payload as string;
      })
      builder
      //Fetch Leave by superwiser Hierarchy
      .addCase(fetchLeaveRequestBySupervisor.pending, (state, action) => {
        state.loadingStates.fetchSupervisor = true;
        state.errors.fetchSupervisor = undefined;
      })
      .addCase(fetchLeaveRequestBySupervisor.fulfilled, (state, action) => {
        state.loadingStates.fetchSupervisor = false;
        state.leaveRequestsBySupervisor = action.payload.data;
      })
      .addCase(fetchLeaveRequestBySupervisor.rejected, (state, action) => {
        state.loadingStates.fetchSupervisor = false;
        state.errors.fetchSupervisor = action.payload as string;
      })

      builder
      //fetch leaveCancellation by supervisor hierarchy 
      .addCase(fetchLeaveCancellationRequestBySupervisor.pending, (state)=>{
        state.loadingStates.fetchCancellationBySupervisor = false;
        state.errors.fetchCancellationBySupervisor = undefined;
      })
      .addCase(fetchLeaveCancellationRequestBySupervisor.fulfilled, (state, action)=>{
        state.loadingStates.fetchCancellationBySupervisor = false;
        state.leaveCancellationRequestBySupervisor = action.payload.data;
      })
      .addCase(fetchLeaveCancellationRequestBySupervisor.rejected, (state, action)=>{
        state.loadingStates.fetchCancellationBySupervisor = false;
        state.errors.fetchCancellationBySupervisor = action.payload as string;
      })

      // Fetch All Leave Requests
      .addCase(fetchAllLeaveRequests.pending, (state) => {
        state.loadingStates.fetchAll = true;
        state.errors.fetchAll = undefined;
      })
      .addCase(fetchAllLeaveRequests.fulfilled, (state, action) => {
        state.loadingStates.fetchAll = false;
        state.leaveRequests = action.payload.data;
      })
      .addCase(fetchAllLeaveRequests.rejected, (state, action) => {
        state.loadingStates.fetchAll = false;
        state.errors.fetchAll = action.payload as string;
      })

      // Fetch Leave Request by Employee ID (new case)
      .addCase(fetchLeaveRequestByEmployeeId.pending, (state) => {
        state.loadingStates.fetchByEmployeeId = true;
        state.errors.fetchByEmployeeId = undefined;
      })
      .addCase(fetchLeaveRequestByEmployeeId.fulfilled, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.employeeLeaveRequest = action.payload.data;
      })
      .addCase(fetchLeaveRequestByEmployeeId.rejected, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.errors.fetchByEmployeeId = action.payload as string;
      })

      //fetch leaveRequestsByHodAndSupervisor
      .addCase(fetchHierarchyLeaveRequests.pending, (state) => {
        state.loadingStates.fetchLeaveRequestByHodAndSupervisor = true;
        state.errors.fetchLeaveRequestByHodAndSupervisor = undefined;
      })
      .addCase(fetchHierarchyLeaveRequests.fulfilled, (state, action) => {
        state.loadingStates.fetchLeaveRequestByHodAndSupervisor = false;
        state.leaveRequestsByHodAndSupervisor = action.payload;
      })
      .addCase(fetchHierarchyLeaveRequests.rejected, (state, action) => {
        state.loadingStates.fetchLeaveRequestByHodAndSupervisor = false;
        state.errors.fetchLeaveRequestByHodAndSupervisor = action.payload as string;
      })
  },
});

export default leaveRequestSlice.reducer;
